import { useCallback, useSyncExternalStore } from "react";

// Which frame a flagged viewer sees (NO-754): the single-rail Always AI shell
// or today's classic chrome. Two per-browser keys, both keyed by the Clerk user
// id so they are readable before /api/me answers, and so a shared machine
// never replays one person's choice for the next sign-in:
//
// - `alwaysai:look:<clerkId>` holds the explicit choice, "classic" or "always".
// - `alwaysai:last-look:<clerkId>` holds the look last rendered. While the
//   viewer and flags load, a viewer who last saw the rail holds a blank frame
//   instead of flashing the classic chrome.
//
// The flag always wins: with `always-brand` off the look is classic whatever
// is stored, and the last-look key is cleared so a once-flagged viewer never
// blank-holds again. Values are only ever the two words (never PHI).

export type ShellLook = "classic" | "always";

export interface LookStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function lookStorageKey(clerkId: string): string {
  return `alwaysai:look:${clerkId}`;
}

export function lastLookStorageKey(clerkId: string): string {
  return `alwaysai:last-look:${clerkId}`;
}

export function parseStoredLook(stored: string | null): ShellLook | null {
  return stored === "classic" || stored === "always" ? stored : null;
}

/** Flag off → classic. Flag on → always, unless the viewer chose classic. */
export function resolveLook({
  flagOn,
  stored,
}: {
  flagOn: boolean;
  stored: string | null;
}): ShellLook {
  if (!flagOn) return "classic";

  return parseStoredLook(stored) === "classic" ? "classic" : "always";
}

// Storage can be missing or throw (private mode, blocked site data); every
// access falls back to "nothing stored".
export function readStoredValue(
  storage: LookStorage | null,
  key: string
): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStoredValue(
  storage: LookStorage | null,
  key: string,
  value: string | null
): void {
  try {
    if (value === null) storage?.removeItem(key);
    else storage?.setItem(key, value);
  } catch {
    // The session still renders; the choice just does not persist.
  }
}

/** The frame kinds a settled workspace renders (see lib/workspace-frame.ts). */
export type RenderedFrameKind = "blank" | "redirect" | "always" | "rd-shell" | "legacy";

/**
 * What to write to the last-look key, or null to leave it. Once flags resolve
 * off the key is cleared (`{ write: null }`); while the flag is on it records
 * the frame actually rendered, but only once the viewer has loaded, because
 * the legacy chrome also stands in while /api/me is pending.
 */
export function lastLookUpdate(input: {
  flagOn: boolean;
  featuresPending: boolean;
  mePending: boolean;
  frame: RenderedFrameKind;
}): { write: ShellLook | null } | null {
  if (input.featuresPending) return null;

  if (!input.flagOn) return { write: null };

  if (input.mePending) return null;

  if (input.frame === "always") return { write: "always" };

  return input.frame === "rd-shell" || input.frame === "legacy"
    ? { write: "classic" }
    : null;
}

/**
 * The profile menu's way to the other look, or null. Only a flagged viewer
 * gets one, and only toward a look that would render: a viewer with nothing
 * for the rail to show has no rail to switch to.
 */
export function lookSwitchTarget(input: {
  flagOn: boolean;
  look: ShellLook;
  railAvailable: boolean;
}): { label: string; target: ShellLook } | null {
  if (!input.flagOn || !input.railAvailable) return null;

  return input.look === "always"
    ? { label: "Switch to the classic look", target: "classic" }
    : { label: "Switch to the Always AI look", target: "always" };
}

const LOOK_CHANGE_EVENT = "alwaysai:look-change";

/** window.localStorage, or null where reading it throws. */
export function browserStorage(): LookStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  window.addEventListener(LOOK_CHANGE_EVENT, onChange);

  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(LOOK_CHANGE_EVENT, onChange);
  };
}

function useStoredValue(key: string | null): string | null {
  return useSyncExternalStore(subscribe, () =>
    key === null ? null : readStoredValue(browserStorage(), key)
  );
}

export interface ShellLookState {
  /** The raw explicit choice; resolve it with the flag through `resolveLook`. */
  stored: string | null;
  lastLook: ShellLook | null;
  chooseLook: (look: ShellLook) => void;
  recordLastLook: (look: ShellLook | null) => void;
}

/** The viewer's stored look and last-rendered look, live across tabs. */
export function useShellLook(clerkId: string | null | undefined): ShellLookState {
  const id = clerkId ?? null;
  const stored = useStoredValue(id === null ? null : lookStorageKey(id));
  const lastLook = parseStoredLook(
    useStoredValue(id === null ? null : lastLookStorageKey(id))
  );

  const chooseLook = useCallback(
    (look: ShellLook) => {
      if (id === null) return;

      writeStoredValue(browserStorage(), lookStorageKey(id), look);
      // `storage` fires only in other tabs; this tab re-reads on its own event.
      window.dispatchEvent(new Event(LOOK_CHANGE_EVENT));
    },
    [id]
  );
  const recordLastLook = useCallback(
    (look: ShellLook | null) => {
      if (id === null) return;

      writeStoredValue(browserStorage(), lastLookStorageKey(id), look);
    },
    [id]
  );

  return { stored, lastLook, chooseLook, recordLastLook };
}
